import styles from './styles.module.scss';
import { Icon } from '@components/icons';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import FormField from '@components/form_field';
import Input from '@components/common/input';
import Select from '@components/common/select';
import { useUserContext } from '@hooks/context/useUserContext';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiUserService } from '@api';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDebounceValue } from 'usehooks-ts';
import { useLocales } from '@hooks/helpers/useLocales';
import { getAllTimezonesOptions } from '@utils/date';
import { useApiQuery } from '@hooks/api/useApiQuery';
import useResponsive from '@hooks/helpers/useResponsive';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

const languages = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'French' }
];

const GeneralSettings = () => {
    const {
        labelLocale,
        submitLocale,
        requestErrorLocale,
        requestSuccessLocale,
        validationLocale
    } = useLocales();
    const { i18n } = useTranslation();
    const { refetchUser, user } = useUserContext();
    const form = useFormContext();
    const watchedTag = useWatch({ name: 'profile.tag', control: form.control });
    const [debouncedTag] = useDebounceValue(watchedTag, 1000);
    const [isEdit, setIsEdit] = useState(false);
    const { isMobile } = useResponsive();

    useApiQuery({
        method: () => apiUserService.checkTag(debouncedTag),
        isInitialRequest: false,
        condition:
            !!debouncedTag && debouncedTag.length >= 5 && debouncedTag !== user?.profile?.tag,
        deps: [debouncedTag],
        onError: () => {
            form.setError('profile.tag', { message: validationLocale('tag_taken') });
        }
    });

    const { handleRequest: handleUpdateTimezone, isLoading } = useApiMutation({
        method: () => {
            const {
                profile: { timezone, tag, lang }
            } = form.getValues();
            const promises = [];
            if (timezone !== user?.profile?.timezone) {
                promises.push(apiUserService.updateTimezone(timezone));
            }
            if (tag !== user?.profile?.tag) {
                promises.push(apiUserService.updateTag(tag));
            }
            if (lang !== user?.profile?.lang) {
                promises.push(apiUserService.updateLang(lang));
            }
            return Promise.all(promises);
        },
        onSuccess: () => {
            requestSuccessLocale('update_profile');
            refetchUser();
            setIsEdit(false);
        },
        onError: requestErrorLocale
    });

    const onSubmit = () => {
        form.trigger().then((isValid) => {
            isValid && handleUpdateTimezone(undefined);
        });
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.left}>
                    <div className={styles.icon}>
                        <Icon name="settings" />
                    </div>
                    <div className={styles.text_wrapper}>
                        <Typography
                            className={styles.title}
                            variant="body1"
                            lh="120%"
                            weight="semibold">
                            General
                        </Typography>
                        <p className={styles.description}>
                            You can change your username, language, and timezone here
                        </p>
                    </div>
                </div>
                {!isEdit && (
                    <Button
                        leftIcon={isEdit ? undefined : 'edit'}
                        variant="outlined-gold"
                        size="small"
                        onClick={() => setIsEdit(true)}>
                        {!isMobile && submitLocale('edit')}
                    </Button>
                )}
            </div>
            <div className={styles.content}>
                {isEdit ? (
                    <>
                        <div className={styles.row}>
                            <div className={styles.data_wrapper}>
                                <FormField<string>
                                    name="profile.tag"
                                    renderComponent={(props) => (
                                        <Input
                                            {...props}
                                            full
                                            suffix="@"
                                            readOnly={!isEdit}
                                            label={labelLocale('username')}
                                        />
                                    )}
                                />
                            </div>
                            <div className={styles.data_wrapper}>
                                <FormField<string | number>
                                    name="profile.timezone"
                                    renderComponent={(props) => (
                                        <Select
                                            {...props}
                                            full
                                            isAllowSearch
                                            readOnly={!isEdit}
                                            label={labelLocale('timezone')}
                                            items={getAllTimezonesOptions()}
                                        />
                                    )}
                                />
                            </div>
                            <div className={styles.data_wrapper}>
                                <FormField<string | number>
                                    name="profile.lang"
                                    renderComponent={(props) => (
                                        <Select
                                            full
                                            isAllowSearch
                                            readOnly={!isEdit}
                                            label={labelLocale('language')}
                                            items={languages}
                                            {...props}
                                            onChange={(value) => {
                                                i18n.changeLanguage(value as string);
                                                // eslint-disable-next-line react/prop-types
                                                props.onChange(value);
                                            }}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        <div className={styles.row}>
                            <div className={styles.data_wrapper}>
                                <p className={styles.label}>Username</p>
                                <p className={styles.value}>{user?.profile?.tag}</p>
                            </div>
                            <div className={styles.data_wrapper}>
                                <p className={styles.label}>Language</p>
                                <p className={styles.value}>
                                    {languages.find((item) => item.value === i18n.language)
                                        ?.label || 'English'}
                                </p>
                            </div>
                            <div className={styles.data_wrapper}>
                                <p className={styles.label}>Timezone</p>
                                <p className={styles.value}>
                                    {
                                        getAllTimezonesOptions().find(
                                            (item) => item.value === user?.profile?.timezone
                                        )?.label
                                    }
                                </p>
                            </div>
                        </div>
                    </>
                )}
                {isEdit && (
                    <div className={styles.actions}>
                        <Button
                            onClick={() => {
                                setIsEdit(false);
                                form.reset(user);
                            }}
                            variant="outlined-primary"
                            size="medium">
                            {submitLocale('cancel')}
                        </Button>
                        <Button
                            isLoading={isLoading}
                            onClick={onSubmit}
                            variant="primary"
                            size="medium">
                            {submitLocale('save_changes')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GeneralSettings;
